import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  getAttemptsLimitMessage,
  getOtpRequestDelaySeconds,
  isAttemptsLimitReachedAtom,
  lastOtpRequestAtom,
  useResendCountdown,
} from '~features/otp';

import { combineValidators } from '~shared/lib/combine-validators';
import { plural } from '~shared/lib/plural';
import { validators } from '~shared/validators';

import { InputPhone } from '@breeze/rhf-adapters';
import { useAtom, useAtomValue } from 'jotai';

import type { OfferFormValues } from '../offer/offer';
import { SubmitButton } from '../submit-button/submit-button';

type FormValues = { offerPhone?: string };

export const OfferDetailsForm = ({
  isLoading,
  onSubmit,
  isSearchOfferError,
}: {
  isLoading: boolean;
  onSubmit: (phone: string) => void;
  isSearchOfferError: boolean;
}) => {
  const { handleSubmit, control, clearErrors, formState, watch, setError } =
    useFormContext<OfferFormValues>();
  const phoneValue = watch('offerPhone');

  const isAttemptsLimitReached = useAtomValue(isAttemptsLimitReachedAtom);
  const [lastOtpRequest] = useAtom(lastOtpRequestAtom);
  const [timer, startCountdown, cancelCountdown] = useResendCountdown();

  const handleFormSubmit = ({ offerPhone }: FormValues) => {
    const otpRequestDelay = getOtpRequestDelaySeconds(lastOtpRequest);
    if (otpRequestDelay) {
      startCountdown(otpRequestDelay);
      return;
    }
    if (offerPhone) {
      onSubmit(offerPhone);
    }
  };

  useEffect(() => {
    if (isSearchOfferError && phoneValue && !isLoading) {
      setError('offerPhone', { message: "Couldn't find any special offers" });
    }
  }, [isSearchOfferError, setError, isLoading, phoneValue]);

  useEffect(() => {
    const otpRequestDelay = getOtpRequestDelaySeconds(lastOtpRequest);

    if (otpRequestDelay) {
      startCountdown(otpRequestDelay);
    }
  }, [lastOtpRequest, startCountdown]);

  useEffect(() => {
    if (timer && isAttemptsLimitReached && !isLoading) {
      const otpRequestDelay = getOtpRequestDelaySeconds(lastOtpRequest);
      setError('offerPhone', {
        message: getAttemptsLimitMessage(otpRequestDelay),
      });
      return;
    }
    if (!isSearchOfferError) {
      clearErrors('offerPhone');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAttemptsLimitReached,
    lastOtpRequest,
    setError,
    clearErrors,
    timer,
    isLoading,
  ]);

  const { isValid } = formState;
  const shouldWait = !!timer && !isAttemptsLimitReached;

  return (
    <form
      onBlur={(event) => event.stopPropagation()}
      onSubmit={handleSubmit(handleFormSubmit)}
      className="w-full"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="w-full">
          <Controller
            name="offerPhone"
            control={control}
            rules={{
              validate: combineValidators(
                validators.phoneLength(),
                validators.mobilePhoneFormat()
              ),
            }}
            render={({ field, ...props }) => (
              <InputPhone
                {...props}
                label="Mobile number"
                field={{
                  ...field,
                  onChange: (...args) => {
                    timer && cancelCountdown();
                    field.onChange(...args);
                  },
                }}
              />
            )}
          />
          {shouldWait && !isLoading && (
            <div className="mb-4">
              <div className="text-s text-text-tertiary">
                Try again in&nbsp;{timer} {plural(timer, 'second', 'seconds')}
              </div>
            </div>
          )}
        </div>
        <SubmitButton
          disabled={(!isValid || !phoneValue || shouldWait) && !isLoading}
          isLoading={isLoading}
        />
      </div>
    </form>
  );
};
